import { useContext } from 'react';
import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import { CustomEditIcon } from '../../../../theme/icons/CustomEditIcon';
import FieldRow from '../components/FieldRow';
import TranslationRow from '../components/TranslationRow';
import { resolveValue } from '../utils/fieldValueUtils';
import { MeContext } from '../../../../context/MeContext';
import type {
  PassContent,
  TemplateField,
  PassTemplateMeta,
} from '../../../../store/types/pass';
import type { FieldLocalisations } from '../../../../store/redux/slices/pass.slice';

interface BackFieldsSectionProps {
  template?: PassTemplateMeta;
  passContent?: PassContent;
  isEdit?: boolean;
  fieldValues?: Record<string, string>;
  errors?: Record<string, string>;
  hasLocalisation?: boolean;
  fieldsWithLocalisation?: Set<string>;
  templateLanguages?: string[];
  fieldLocalisations?: FieldLocalisations;
  expandedTranslations?: Record<string, boolean>;
  onFieldChange?: (fieldId: string, value: string) => void;
  onLocalisationChange?: (
    fieldId: string,
    locale: string,
    value: string
  ) => void;
  onAddTranslations?: (fieldId: string) => void;
  onRemoveTranslations?: (fieldId: string) => void;
  onFocus?: (fieldId: string, locale?: string | null) => void;
  focusedFieldId?: string | null;
  focusedLocale?: string | null;
}

const BackFieldsSection = ({
  template,
  passContent,
  isEdit,
  fieldValues,
  errors = {},
  hasLocalisation,
  fieldsWithLocalisation,
  templateLanguages = [],
  fieldLocalisations = {},
  expandedTranslations = {},
  onFieldChange,
  onLocalisationChange,
  onAddTranslations,
  onRemoveTranslations,
  onFocus,
  focusedFieldId,
  focusedLocale,
}: BackFieldsSectionProps) => {
  const meCtx = useContext(MeContext) as { state: Record<string, unknown> } | undefined;
  const expressivePassRolloutEnabled = meCtx?.state?.expressivePassRolloutEnabled === true;

  if (!template) return null;

  const fields: TemplateField[] = [
    template.back_fields.back_one,
    template.back_fields.back_two,
    template.back_fields.back_three,
    template.back_fields.back_four,
    template.back_fields.back_five,
  ];

  const activeFields = fields.filter((f) => f.id);

  if (activeFields.length === 0) return null;

  return (
    <SectionWrapper
      title={expressivePassRolloutEnabled ? 'Additional fields' : 'Back fields'}
      subtitle={
        <Text fontSize="15px" color="grey">
            {hasLocalisation
              ? `These fields appear on the ${expressivePassRolloutEnabled ? 'back of the pass for Apple and further down on scroll for Google' : 'back of the pass'}. Add a translation for each language enabled on this template`
              : `These fields appear on the ${expressivePassRolloutEnabled ? 'back of the pass for Apple and further down on scroll for Google' : 'back of the pass'}`}
        </Text>
      }
      openOnLoad
      id="back-fields-section"
    >
      <Box display="flex" flexDirection="column" gap={4}>
        {activeFields.map((field) => {
          const isExpanded = expandedTranslations[field.id];
          const fieldTranslations = fieldLocalisations[field.id] || {};

          return (
            <Box key={field.id}>
              <FieldRow
                label={field.label || field.id}
                value={
                  fieldValues?.[field.id] ?? resolveValue(field, passContent)
                }
                error={errors[`fieldValues.${field.id}`]}
                isHighlighted={focusedFieldId === field.id && !focusedLocale}
                onFocus={() => onFocus?.(field.id, null)}
                onChange={(value) => onFieldChange?.(field.id, value)}
              />

              {hasLocalisation && templateLanguages.length > 0 && fieldsWithLocalisation?.has(field.id) && (
                <>
                  {isExpanded ? (
                    <Box display="flex" flexDirection="column" gap={2} mt={2}>
                      {templateLanguages.map((locale) => {
                        const hasAnyTranslation = templateLanguages.some((l) => l !== locale && fieldTranslations[l]?.trim());
                        return (
                        <TranslationRow
                          key={locale}
                          locale={locale}
                          value={fieldTranslations[locale] || ''}
                          required={hasAnyTranslation && !fieldTranslations[locale]?.trim()}
                          error={
                            errors[`localisation.${field.id}.${locale}`]
                              ? 'Translation is required'
                              : undefined
                          }
                          isHighlighted={focusedFieldId === field.id && focusedLocale === locale}
                          onFocus={() => onFocus?.(field.id, locale)}
                          onChange={(value) =>
                            onLocalisationChange?.(field.id, locale, value)
                          }
                        />
                      );
                      })}
                      <Text
                        fontSize="12px"
                        color="#FF4935"
                        textAlign="right"
                        cursor="pointer"
                        mt={3}
                        onClick={() => onRemoveTranslations?.(field.id)}
                        _hover={{ opacity: 0.8 }}
                      >
                        Remove translations
                      </Text>
                    </Box>
                  ) : (
                    <Flex
                      align="center"
                      gap={1.5}
                      fontSize="15px"
                      color="inkBlue"
                      cursor="pointer"
                      mt={3}
                      pl={2}
                      onClick={() => onAddTranslations?.(field.id)}
                      _hover={{ opacity: 0.7 }}
                    >
                      {isEdit ? (
                        <>
                          <CustomEditIcon boxSize="14px" />
                          Edit translation
                        </>
                      ) : (
                        '+ Add translation'
                      )}
                    </Flex>
                  )}
                </>
              )}

              {hasLocalisation && fieldsWithLocalisation?.has(field.id) && isExpanded && <Divider mt={4} />}
              {hasLocalisation && fieldsWithLocalisation?.has(field.id) && !isExpanded && <Box mb={3} />}
            </Box>
          );
        })}
      </Box>
    </SectionWrapper>
  );
};

export default BackFieldsSection;
